import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigationType, useLocation } from "react-router-dom";
import { VoiceProvider } from '@humeai/voice-react';
import GameArea from "./pages/GameArea";
import ExerciseDashboard from './Exercise/ExerciseDashboard';
import ExerciseTrainDashboard from './exerciseTrain/ExerciseTrainDashboard';
import PatientDashboard from "./dashboard/PatientDashboard";
import ClinicianDashboard from "./dashboard/ClinicianDashboard";
import SuperAdminDashboard from "./superadmin/SuperAdminDashboard";
import EmailScreen from "./auth/screens/EmailScreen";
import AnalyticsDashboard from "./components/AnalyticsDashboard";
import ProtectedRoute from "./components/ProtectedRoute";
import analyticsService from './services/AnalyticsService';
import useAnalytics from './hooks/useAnalytics';
import ConnectionTest from './utils/ConnectionTest';
import QuickConnectionTest from './utils/QuickConnectionTest';

function AppRoutes() {
    const action = useNavigationType();
    const location = useLocation();
    const pathname = location.pathname;
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const { trackLogout, getUserName } = useAnalytics();

    // Check authentication status
    const checkAuth = () => {
        const user = localStorage.getItem('user');
        
        if (user) {
            try {
                const userData = JSON.parse(user);
                const isAuth = userData.isAuthenticated === true;
                setIsAuthenticated(isAuth);
            } catch (error) {
                console.error('Error parsing user data:', error);
                localStorage.removeItem('user');
                setIsAuthenticated(false);
            }
        } else {
            setIsAuthenticated(false);
        }
        setLoading(false);
    };

    useEffect(() => {
        checkAuth();

        // Listen for storage changes (when user logs in from EmailScreen)
        const handleStorageChange = (e) => {
            if (e.key === 'user') {
                checkAuth();
            }
        };

        // Listen for custom login event
        const handleLoginEvent = () => {
            checkAuth();
        };

        // Handle browser close/refresh - track logout for analytics
        const handleBeforeUnload = () => {
            const currentUser = getUserName();
            if (currentUser && currentUser !== 'unknown') {
                // Use beacon mode for reliable tracking during page unload
                trackLogout(currentUser, true);
            }
        };

        // Handle visibility change (tab switch, minimize) - track logout
        const handleVisibilityChange = () => {
            if (document.hidden) {
                const currentUser = getUserName();
                if (currentUser && currentUser !== 'unknown') {
                    trackLogout(currentUser);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('userLogin', handleLoginEvent);
        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('userLogin', handleLoginEvent);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [trackLogout, getUserName]);

    useEffect(() => {
        if (action !== "POP") {
            window.scrollTo(0, 0);
        }
    }, [action, pathname]);

    useEffect(() => {
        if (pathname === "/apple") {
            console.log("it's /apple");
        }
        let title = "Games";
        let metaDescription = "Games";
        
        // Update title based on current route
        if (pathname.startsWith("/dashboard")) {
            title = "Dashboard - MoveMend";
            metaDescription = "MoveMend Dashboard";
        }
        
        if (title) {
            document.title = title;
        }
        if (metaDescription) {
            const metaDescriptionTag = document.querySelector('head > meta[name="description"]');
            if (metaDescriptionTag) {
                metaDescriptionTag.content = metaDescription;
            }
        }
    }, [pathname]);

    // Show loading screen while checking authentication
    if (loading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                fontFamily: 'Scope One, serif',
                fontSize: '18px',
                color: '#6D4F79'
            }}>
                Loading...
            </div>
        );
    }

    // Show login screen if not authenticated
    if (!isAuthenticated) {
        return <EmailScreen />;
    }

    // Show main app if authenticated
    return (
        <Routes>
            {/* Dashboard Routes */}
            <Route
                path="/dashboard/patient"
                element={
                    <div className="patient-dashboard-section">
                        <PatientDashboard />
                    </div>
                }
            />
            <Route
                path="/dashboard/clinician"
                element={
                    <ProtectedRoute requiredRole="clinician">
                        <div className="clinician-dashboard-section">
                            <ClinicianDashboard />
                        </div>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/dashboard/superadmin"
                element={
                    <ProtectedRoute requiredRole="superadmin">
                        <div className="clinician-dashboard-section">
                            <SuperAdminDashboard />
                        </div>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/analytics"
                element={
                    <div className="clinician-dashboard-section">
                        <AnalyticsDashboard />
                    </div>
                }
            />
            
            {/* Default route shows patient dashboard */}
            <Route
                path="/"
                element={
                    <div className="patient-dashboard-section">
                        <PatientDashboard />
                    </div>
                }
            />
            
            {/* Game Routes */}
            <Route
                path="/apple"
                element={
                    <div className="game-section">
                        <GameArea game_type="Apple Pick"/>
                    </div>
                }
            />
            <Route
                path="/boxing"
                element={
                    <div className="game-section">
                        <GameArea game_type="Super Punch"/>
                    </div>
                }
            />
            <Route
                path="/potato"
                element={
                    <div className="game-section">
                        <GameArea game_type="Hot Potato"/>
                    </div>
                }
            />
            <Route
                path="/row"
                element={
                    <div className="game-section">
                        <GameArea game_type="Ocean Row"/>
                    </div>
                }
            />  
            <Route
                path="/sky"
                element={
                    <div className="game-section">
                        <GameArea game_type="Sky Flight"/>
                    </div>
                }
            />
            <Route
                path="/tomato"
                element={
                    <div className="game-section">
                        <GameArea game_type="Tomato Dodge"/>
                    </div>
                }
            />
            <Route
                path="/garden"
                element={
                    <div className="game-section">
                        <GameArea game_type="Plant Care"/>
                    </div>
                }
            />
            <Route
                path="/berry"
                element={
                    <div className="game-section">
                        <GameArea game_type="Berry Picking"/>
                    </div>
                }
            />
            <Route
                path="/kick"
                element={
                    <div className="game-section">
                        <GameArea game_type="Soccer Kick"/>
                    </div>
                }
            />
            <Route
                path="/skate"
                element={
                    <div className="game-section">
                        <GameArea game_type="Ice Skate"/>
                    </div>
                }
            />
            <Route path="/exercise-dashboard" element={<ExerciseDashboard />} />
            <Route path="/exercise-train" element={<ExerciseTrainDashboard />} />
        </Routes>
    );
}

function App() {
    const apiKey = process.env.REACT_APP_HUME_API_KEY;
    const configId = process.env.REACT_APP_HUME_CONFIG_ID;
    
    // Environment variables loaded
    
    return (
        <VoiceProvider
            auth={{ type: 'apiKey', value: apiKey }}
            configId={configId}
            debug={false} // Set to true for debugging
            onError={(error) => {
                console.error('HUME Voice error:', error);
            }}
            messageHistoryLimit={10} // Keep minimal history for one-way TTS
            clearMessagesOnDisconnect={true}
        >
            <AppRoutes />
        </VoiceProvider>
    );
}

export default App;